import React, { useState, useEffect, useRef } from 'react';
import dailyVideoService from '../../services/dailyVideoService';
import './LiveVideoClassroom.css';

const LiveVideoClassroom = ({ sessionId, userId, userName, userType, isInstructor = false }) => {
  const [isConfigured, setIsConfigured] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [error, setError] = useState('');
  const [participants, setParticipants] = useState([]);
  const [localParticipant, setLocalParticipant] = useState(null);
  const [isMicEnabled, setIsMicEnabled] = useState(!isInstructor); // Students muted by default
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [roomUrl, setRoomUrl] = useState('');
  const videoContainerRef = useRef(null);
  const callFrameRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    // Check if Daily.co is configured
    setIsConfigured(dailyVideoService.isConfigured());

    if (!dailyVideoService.isConfigured()) {
      setError('Video service not configured. Please contact administrator.');
      return;
    }

    // Initialize room
    if (isMounted) {
      initializeRoom();
    }

    return () => {
      isMounted = false;
      // Cleanup on unmount
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const initializeRoom = async () => {
    try {
      // Clean up any existing call frame first
      cleanup();

      setIsJoining(true);
      setError('');

      // SECURITY: Get or create room for this session (only admin/coach can create)
      const room = await dailyVideoService.getOrCreateRoom(sessionId, userType, userId);
      setRoomUrl(room.url);

      // Initialize call frame with parent element
      const callFrame = dailyVideoService.initializeCallFrame(videoContainerRef.current, {
        showLeaveButton: true,
        iframeStyle: {
          position: 'relative',
          width: '100%',
          height: '100%',
          border: '0',
          borderRadius: '8px'
        }
      });

      callFrameRef.current = callFrame;

      // If frame wasn't automatically attached, mount it
      if (videoContainerRef.current && !videoContainerRef.current.contains(callFrame.iframe())) {
        const iframe = callFrame.iframe();
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        videoContainerRef.current.appendChild(iframe);
      }

      // Set up event handlers
      setupEventHandlers(callFrame);

      // Join the room (pass room object for token generation)
      await dailyVideoService.joinRoom(room.url, {
        userId,
        userName: userName || 'Student',
        userType,
        sessionId, // Pass sessionId for security logging
        roomName: room.name || room.config?.name, // Pass room name for token generation
        startVideoOff: !isVideoEnabled,
        startAudioOff: !isMicEnabled
      });

      setIsJoined(true);
      setIsJoining(false);
    } catch (err) {
      console.error('Error initializing room:', err);
      setError(err.message || 'Failed to join video room. Please try again.');
      setIsJoining(false);
    }
  };

  const setupEventHandlers = (callFrame) => {
    // SECURITY: Auto-disconnect after max duration (2 hours)
    const maxDuration = 2 * 60 * 60 * 1000; // 2 hours
    window.videoDisconnectTimer = setTimeout(() => {
      if (isJoined) {
        setError('Session time limit reached (2 hours). Please reconnect if needed.');
        handleLeave();
      }
    }, maxDuration);

    // Participant joined
    callFrame.on('participant-joined', (event) => {
      updateParticipants();
    });

    // Participant left
    callFrame.on('participant-left', (event) => {
      updateParticipants();
    });

    // Local audio/video state changed
    callFrame.on('local-audio-started', () => {
      setIsMicEnabled(true);
    });

    callFrame.on('local-audio-stopped', () => {
      setIsMicEnabled(false);
    });

    callFrame.on('local-video-started', () => {
      setIsVideoEnabled(true);
    });

    callFrame.on('local-video-stopped', () => {
      setIsVideoEnabled(false);
    });

    // Screen share started/stopped
    callFrame.on('screen-share-started', () => {
      setIsScreenSharing(true);
    });

    callFrame.on('screen-share-stopped', () => {
      setIsScreenSharing(false);
    });

    // Error handling
    callFrame.on('error', (event) => {
      console.error('Daily.co error:', event);
      setError(event.error?.message || 'An error occurred. Please try again.');
    });

    // Network quality
    callFrame.on('network-quality-change', (event) => {
      // You can show network quality indicator
      console.log('Network quality:', event.quality);
    });
  };

  const updateParticipants = () => {
    if (callFrameRef.current) {
      const allParticipants = callFrameRef.current.participants();
      setParticipants(Object.values(allParticipants).filter(p => p.user_id !== 'local'));
      setLocalParticipant(allParticipants.local);
    }
  };

  const handleToggleMic = async () => {
    try {
      await dailyVideoService.setMicrophoneEnabled(!isMicEnabled);
      setIsMicEnabled(!isMicEnabled);
    } catch (err) {
      console.error('Error toggling microphone:', err);
      setError('Failed to toggle microphone. Please try again.');
    }
  };

  const handleToggleVideo = async () => {
    try {
      await dailyVideoService.setCameraEnabled(!isVideoEnabled);
      setIsVideoEnabled(!isVideoEnabled);
    } catch (err) {
      console.error('Error toggling video:', err);
      setError('Failed to toggle video. Please try again.');
    }
  };

  const handleScreenShare = async () => {
    try {
      if (isScreenSharing) {
        await dailyVideoService.stopScreenShare();
        setIsScreenSharing(false);
      } else {
        await dailyVideoService.startScreenShare();
        setIsScreenSharing(true);
      }
    } catch (err) {
      console.error('Error toggling screen share:', err);
      setError('Failed to start screen share. Please check permissions.');
    }
  };

  const handleLeave = async () => {
    try {
      await dailyVideoService.leaveRoom();
      setIsJoined(false);
      cleanup();
    } catch (err) {
      console.error('Error leaving room:', err);
    }
  };

  const cleanup = () => {
    try {
      // Clear any timers
      if (window.videoDisconnectTimer) {
        clearTimeout(window.videoDisconnectTimer);
        window.videoDisconnectTimer = null;
      }

      // Leave room if joined
      if (callFrameRef.current && isJoined) {
        dailyVideoService.leaveRoom().catch(err => 
          console.warn('Error leaving room during cleanup:', err)
        );
      }
      
      // Destroy call frame
      if (callFrameRef.current) {
        dailyVideoService.destroy();
        callFrameRef.current = null;
      }
      
      // Clear container
      if (videoContainerRef.current) {
        videoContainerRef.current.innerHTML = '';
      }
    } catch (error) {
      console.warn('Error during cleanup:', error);
    }
  };

  if (!isConfigured) {
    return (
      <div className="live-video-classroom">
        <div className="video-not-configured">
          <h3>⚠️ Video Service Not Configured</h3>
          <p>Daily.co video service is not configured. Please contact your administrator.</p>
          <p className="config-hint">
            To enable video, add <code>REACT_APP_DAILY_API_KEY</code> to your .env file.
          </p>
        </div>
      </div>
    );
  }

  if (error && !isJoined) {
    return (
      <div className="live-video-classroom">
        <div className="video-error">
          <h3>❌ Error</h3>
          <p>{error}</p>
          <button onClick={initializeRoom} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="live-video-classroom">
      {isJoining && (
        <div className="video-loading">
          <div className="loading-spinner"></div>
          <p>Joining video room...</p>
        </div>
      )}

      {isJoined && (
        <>
          <div className="video-container" ref={videoContainerRef}>
            {/* Daily.co iframe will be mounted here */}
          </div>

          <div className="video-controls">
            <button
              className={`control-btn ${isMicEnabled ? 'active' : 'muted'}`}
              onClick={handleToggleMic}
              title={isMicEnabled ? 'Mute' : 'Unmute'}
            >
              {isMicEnabled ? '🎤' : '🔇'}
            </button>

            <button
              className={`control-btn ${isVideoEnabled ? 'active' : 'disabled'}`}
              onClick={handleToggleVideo}
              title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
            >
              {isVideoEnabled ? '📹' : '📷'}
            </button>

            {isInstructor && (
              <button
                className={`control-btn ${isScreenSharing ? 'active' : ''}`}
                onClick={handleScreenShare}
                title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
              >
                {isScreenSharing ? '🖥️' : '🖥️'}
              </button>
            )}

            <div className="participant-count">
              👥 {participants.length + 1} {participants.length === 0 ? 'person' : 'people'}
            </div>

            <button
              className="control-btn leave-btn"
              onClick={handleLeave}
              title="Leave room"
            >
              🚪 Leave
            </button>
          </div>

          {error && (
            <div className="video-error-banner">
              <span>⚠️ {error}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LiveVideoClassroom;

